import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Box,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import NotificationSnackbar from "./NotificationBar";

function Addnewcreditor({ open, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    calculationMethod: "",
    basePayroll: "",
    rate: "",
    bank: "",
    bankAccountNumber: "",
    bankAccountName:"",
  });

  const [calculationMethods, setCalculationMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [basePayrolls, setBasePayrolls] = useState([]);

  const theme = useTheme();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  useEffect(() => {
    if (open) {
      axios
        .get("/api/support/creditors-details")
        .then((res) => {
          setCalculationMethods(res.data.calculationMethods);
          setBanks(res.data.banks);
          setBasePayrolls(res.data.basePayrollComponents);
        })
        .catch((err) => console.error(err));
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm({
        name: "",
        calculationMethod: "",
        basePayroll: "",
        rate: "",
        bank: "",
        bankAccountNumber: "",
      });
    }
  }, [open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const selectedCalcMethod = useMemo(() => {
    return calculationMethods.find((m) => m.id === form.calculationMethod);
  }, [calculationMethods, form.calculationMethod]);

  const isFixedAmount = selectedCalcMethod?.name
    ?.toLowerCase()
    .includes("fixed");

  const handleSubmit = async () => {
    try {
      const res = await fetch("/api/support/creditors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: form.name,
          calculationMethodId: form.calculationMethod,
          basePayrollComponentId: form.basePayroll,
          rate: isFixedAmount ? 0 : form.rate,
          bankId: form.bank,
          accountNumber: form.bankAccountNumber,
          accountName: form.bankAccountName,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Failed to save creditor");
      }
      setSnackbarMessage(data?.message || "Creditor saved successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onSave?.();
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Save creditor failed:", error);

      setSnackbarMessage(error.message || "Failed to save creditor");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const isFormValid = useMemo(() => {
    if (!form.name) return false;
    if (!form.calculationMethod) return false;
    if (!form.basePayroll) return false;
    if (!form.bank) return false;
    if (!form.bankAccountNumber) return false;
    if (!isFixedAmount && !form.rate) return false;

    return true;
  }, [form, isFixedAmount]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          bgcolor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
        }}
      >
        Add New Creditor
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
          <TextField
            label="Creditor Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Calculation Method</InputLabel>
            <Select
              name="calculationMethod"
              value={form.calculationMethod}
              label="Calculation Method"
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  calculationMethod: value,
                  rate: "",
                }));
              }}
            >
              {calculationMethods.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Base Payroll</InputLabel>
            <Select
              name="basePayroll"
              value={form.basePayroll}
              label="Base Payroll"
              onChange={handleChange}
            >
              {basePayrolls.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ✅ HIDE RATE WHEN FIXED AMOUNT */}
          {!isFixedAmount && (
            <TextField
              label="Rate"
              name="rate"
              value={form.rate}
              onChange={handleChange}
              fullWidth
            />
          )}

          {/* ✅ SEARCHABLE BANK DROPDOWN */}
          <Autocomplete
            options={banks}
            getOptionLabel={(option) => option?.name || ""}
            value={banks.find((b) => b.id === form.bank) || null}
            onChange={(e, newValue) => {
              setForm((prev) => ({ ...prev, bank: newValue?.id || "" }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Bank" fullWidth />
            )}
          />

          <TextField
            label="Bank Account Number"
            name="bankAccountNumber"
            value={form.bankAccountNumber}
            onChange={handleChange}
            fullWidth
          />
            <TextField
            label="Bank Account Name"
            name="bankAccountName"
            value={form.bankAccountName}
            onChange={handleChange}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          Save
        </Button>
      </DialogActions>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default Addnewcreditor;
